import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ProgressBar, Tab, Tabs} from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import {AnimatePresence, motion} from "framer-motion";
import {ACHIEVEMENT_IMAGES, MATCH_RESULTS} from "../../../util/Constants";
import {playerToString} from "../../../util/StringUtils";
import {useIsMobile} from "../../../hooks/useIsMobile";
import {useConfetti} from "../../../hooks/useConfetti";
import {useAwardsContext} from "../../../hooks/queries/useTournament";
import MatchBox from "../../matches/MatchBox";
import {computeTournamentAggregates, pickFunFact} from "./awardsFunFacts";
import type {HallOfFameRecord, Match, PlayerAwardsContext, PlayerScore, TournamentAchievement, TournamentDetail, TournamentRound} from "../../../types/domain";

interface AwardsCeremonyTabProps {
  tournament: TournamentDetail;
  /** Whether this tab is currently visible. React-Bootstrap Tabs keeps all panes mounted,
   * so without this the arrow-key handler and confetti would fire from other tabs too. */
  isActive: boolean;
}

const JERSEY_COLORS = [
  "#e63946", // red
  "#06d6a0", // teal-green
  "#118ab2", // blue
  "#ffd166", // yellow
  "#f77f00", // orange
  "#9d4edd", // purple
  "#ef476f", // pink
];

/* Cheering fan with a colored jersey torso. Head/arms/legs use currentColor so the silhouette
 * still reads against the page; only the torso gets the team color. */
function FanSilhouette({size, jerseyColor}: {size: number; jerseyColor: string}) {
  return (
    <svg viewBox="0 0 50 90" width={size} height={size * 1.8} aria-hidden="true">
      {/* Head */}
      <circle cx="25" cy="11" r="7.5" fill="currentColor" />
      {/* Left arm + fist */}
      <path d="M 18 22 L 9 4 L 5 7 L 15 24 Z" fill="currentColor" />
      <circle cx="6" cy="4" r="3.5" fill="currentColor" />
      {/* Right arm + fist */}
      <path d="M 32 22 L 41 4 L 45 7 L 35 24 Z" fill="currentColor" />
      <circle cx="44" cy="4" r="3.5" fill="currentColor" />
      {/* Jersey (torso) */}
      <path d="M 16 21 Q 25 19 34 21 L 33 56 L 17 56 Z" fill={jerseyColor} />
      {/* Legs */}
      <path d="M 17 55 L 16 88 Q 16 90 18 90 L 22 90 Q 24 90 24 88 L 25 64 L 26 88 Q 26 90 28 90 L 32 90 Q 34 90 34 88 L 33 55 Z" fill="currentColor" />
    </svg>
  );
}

interface IntroStats {
  matches: number;
  sets: number;
  points: number;
}

function computeIntroStats(tournament: TournamentDetail): IntroStats {
  let matches = 0;
  let sets = 0;
  let points = 0;
  for (const r of tournament.rounds) {
    for (const m of r.matches) {
      if (m.state !== MATCH_RESULTS.PLAYER_ONE_WON && m.state !== MATCH_RESULTS.PLAYER_TWO_WON) continue;
      matches++;
      sets += m.results.length;
      for (const result of m.results) {
        points += result.playerOneScore + result.playerTwoScore;
      }
    }
  }
  return {matches, sets, points};
}

function AwardsCeremonyTab({tournament, isActive}: AwardsCeremonyTabProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { fireWinConfetti, fireTournamentCompleteConfetti } = useConfetti();

  // finalPlacements is in best→worst order; reverse so the ceremony starts at last place.
  const orderedPlacements = useMemo<PlayerScore[]>(
    () => [...tournament.finalPlacements].reverse(),
    [tournament.finalPlacements]
  );

  // Page indexing: 0 = intro, 1..totalPlayers = player pages (last place → first place).
  const totalPlayers = orderedPlacements.length;
  const totalPages = totalPlayers + 1;
  const [currentIndex, setCurrentIndex] = useState(0);
  // +1 → next slides in from right; -1 → previous slides in from left.
  const directionRef = useRef<1 | -1>(1);

  useEffect(() => {
    setCurrentIndex(0);
  }, [tournament.id]);

  const goPrev = () => {
    directionRef.current = -1;
    setCurrentIndex(i => Math.max(0, i - 1));
  };
  const goNext = () => {
    directionRef.current = 1;
    setCurrentIndex(i => Math.min(totalPages - 1, i + 1));
  };

  const isIntro = currentIndex === 0;
  const playerIdx = currentIndex - 1; // -1 for intro
  const currentScore = isIntro ? null : orderedPlacements[playerIdx];
  const currentPlayer = currentScore?.player ?? null;
  const placement = isIntro ? 0 : totalPlayers - playerIdx;

  const playerMatches = useMemo<Match[]>(() => {
    if (!currentPlayer) return [];
    const matches: Match[] = [];
    tournament.rounds.forEach((round: TournamentRound) => {
      round.matches.forEach((match: Match) => {
        if (match.playerOne.id === currentPlayer.id || match.playerTwo.id === currentPlayer.id) {
          matches.push(match);
        }
      });
    });
    return matches.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [currentPlayer, tournament.rounds]);

  const playerAchievements = useMemo<TournamentAchievement[]>(() => {
    if (!currentPlayer) return [];
    return tournament.achievements.filter(a => a.player.id === currentPlayer.id);
  }, [currentPlayer, tournament.achievements]);

  const aggregates = useMemo(() => computeTournamentAggregates(tournament), [tournament]);
  const introStats = useMemo(() => computeIntroStats(tournament), [tournament]);
  const {data: awardsContextList} = useAwardsContext(tournament.id);
  const awardsContextByPlayer = useMemo(() => {
    const map = new Map<number, PlayerAwardsContext>();
    (awardsContextList ?? []).forEach(ctx => map.set(ctx.playerId, ctx));
    return map;
  }, [awardsContextList]);

  const ladderNameKey = tournament.ladder.name ?? "";
  const funFact = currentScore && currentPlayer
    ? pickFunFact({
        player: currentPlayer,
        score: currentScore,
        placement,
        totalPlayers,
        aggregates,
        awardsContext: awardsContextByPlayer.get(currentPlayer.id) ?? null,
        ladderNameKey,
        tournament,
      })
    : null;

  // Confetti on every player-page transition. 1st place gets the bigger burst; intro never fires.
  // Guarded on isActive so a background tab doesn't paint confetti over the currently visible one.
  const prevIndexRef = useRef<number>(currentIndex);
  useEffect(() => {
    const prev = prevIndexRef.current;
    prevIndexRef.current = currentIndex;
    if (prev === currentIndex) return;
    if (!isActive) return;
    if (currentIndex === 0) return; // intro
    const placementOnPage = totalPlayers - (currentIndex - 1);
    if (placementOnPage === 1) {
      fireTournamentCompleteConfetti();
    } else {
      fireWinConfetti();
    }
  }, [currentIndex, totalPlayers, isActive, fireWinConfetti, fireTournamentCompleteConfetti]);

  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowRight") {
        directionRef.current = 1;
        setCurrentIndex(i => Math.min(totalPages - 1, i + 1));
      } else if (e.key === "ArrowLeft") {
        directionRef.current = -1;
        setCurrentIndex(i => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [totalPages, isActive]);

  if (totalPlayers === 0) {
    return <div className="mt-4 text-center"><h4>{t("noFinalPlacements")}</h4></div>;
  }

  const allTopMatches: HallOfFameRecord[] = tournament.topMatches ?? [];
  const topMatchesLeft = allTopMatches.slice(0, 3);
  const topMatchesRight = allTopMatches.slice(3, 6);

  const renderTopMatchRecord = (rec: HallOfFameRecord) => (
    <div key={"intro-rec-" + rec.category} className="awards-intro-record">
      <div className="awards-intro-record-label">{t("record_" + rec.category)}</div>
      {rec.entries.map((entry, i) => entry.match && (
        <div className="compact-matchbox" key={"intro-rec-mb-" + rec.category + "-" + i}>
          <MatchBox match={entry.match} showDetailButton={false} />
        </div>
      ))}
    </div>
  );

  const crowdSizes = isMobile
    ? [28, 36, 30, 40, 32, 38, 28]
    : [55, 75, 60, 80, 65, 70, 55];

  const playersCount = totalPlayers.toLocaleString();
  const matchesCount = introStats.matches.toLocaleString();
  const setsCount = introStats.sets.toLocaleString();
  const pointsCount = introStats.points.toLocaleString();
  const achievementsCount = tournament.achievements.length.toLocaleString();

  const summarySentence = i18n.language === "cs" ? (
    <>
      Celkem <span className="highlight">{playersCount}</span> hráčů během <span className="highlight">{matchesCount}</span> zápasů
      a <span className="highlight">{setsCount}</span> setů odehrálo <span className="highlight">{pointsCount}</span> výměn,
      za které bylo uděleno <span className="highlight">{achievementsCount}</span> ocenění.
    </>
  ) : (
    <>
      A total of <span className="highlight">{playersCount}</span> players played <span className="highlight">{matchesCount}</span> matches
      and <span className="highlight">{setsCount}</span> sets across <span className="highlight">{pointsCount}</span> rallies,
      earning <span className="highlight">{achievementsCount}</span> awards along the way.
    </>
  );

  const introPage = (
    <div className="awards-intro">
      {!isMobile && (
        <div className="awards-intro-side awards-intro-side-left">
          {topMatchesLeft.map(renderTopMatchRecord)}
        </div>
      )}
      <div className="awards-intro-center">
        <div className="awards-intro-title">{t("awardsIntro__title")}</div>
        <div className="awards-intro-tournament">{tournament.name}</div>
        <div className="fun-stats-sentence awards-intro-summary">{summarySentence}</div>
        <div className="awards-intro-crowd" aria-hidden="true">
          {crowdSizes.map((size, i) => (
            <div className="awards-crowd-figure" key={i} style={{animationDelay: `${-i * 0.18}s`}}>
              <FanSilhouette size={size} jerseyColor={JERSEY_COLORS[i % JERSEY_COLORS.length]} />
            </div>
          ))}
        </div>
        <div className="awards-intro-hint">{t("awardsIntro__hint")}</div>
      </div>
      {!isMobile && (
        <div className="awards-intro-side awards-intro-side-right">
          {topMatchesRight.map(renderTopMatchRecord)}
        </div>
      )}
    </div>
  );

  // === Player page render ===

  const matchesPanel = currentPlayer ? (
    <div className={isMobile ? "awards-matches-panel" : "awards-matches-panel awards-matches-panel-grid"}>
      {playerMatches.length === 0 ? <h6>{t("noMatchesYet")}</h6> : null}
      {playerMatches.map(m => (
        <div className="compact-matchbox" key={"awards-match-" + m.id}>
          <MatchBox
            match={m}
            showDetailButton={false}
            perspectivePlayerId={currentPlayer.id}
          />
        </div>
      ))}
    </div>
  ) : null;

  const matchesPlayed = currentScore ? currentScore.matchesWon + currentScore.matchesLost : 0;
  const setsPlayed = currentScore ? currentScore.setsWon + currentScore.setsLost : 0;
  const pointsPlayed = currentScore ? currentScore.pointsWon + currentScore.pointsLost : 0;

  const statsPanel = currentScore ? (
    <div className="awards-stats-panel">
      <div className="awards-stats-block">
        <div className="awards-stats-progress-label">{t("matches")}</div>
        <ProgressBar max={200}>
          <ProgressBar label={currentScore.matchesWon} variant="success" now={currentScore.matchesWon} key={1} max={matchesPlayed > 0 ? matchesPlayed : 1} />
          <ProgressBar label={currentScore.matchesLost} variant="danger" now={currentScore.matchesLost} key={2} max={matchesPlayed > 0 ? matchesPlayed : 1} />
        </ProgressBar>
        <div className="awards-stats-progress-label">{t("sets")}</div>
        <ProgressBar max={200}>
          <ProgressBar label={currentScore.setsWon} variant="success" now={currentScore.setsWon} key={1} max={setsPlayed > 0 ? setsPlayed : 1} />
          <ProgressBar label={currentScore.setsLost} variant="danger" now={currentScore.setsLost} key={2} max={setsPlayed > 0 ? setsPlayed : 1} />
        </ProgressBar>
        <div className="awards-stats-progress-label">{t("points")}</div>
        <ProgressBar max={200}>
          <ProgressBar label={currentScore.pointsWon} variant="success" now={currentScore.pointsWon} key={1} max={pointsPlayed > 0 ? pointsPlayed : 1} />
          <ProgressBar label={currentScore.pointsLost} variant="danger" now={currentScore.pointsLost} key={2} max={pointsPlayed > 0 ? pointsPlayed : 1} />
        </ProgressBar>
      </div>

      <div className="awards-achievements-block">
        <h5 className="mt-4 mb-3">{t("achievements")}</h5>
        {playerAchievements.length === 0
          ? <div style={{color: "var(--text-muted)"}}>{t("noAchievementsThisTournament")}</div>
          : null}
        {playerAchievements.map(a => (
          <div className="awards-achievement-row" key={"awards-ach-" + a.id}>
            <img
              src={ACHIEVEMENT_IMAGES[a.achievement as keyof typeof ACHIEVEMENT_IMAGES]}
              className="achievement-image"
              alt={a.achievement}
            />
            <div className="ms-3">
              <div><b>{t("achievementTitle__" + a.achievement)}</b></div>
              <div style={{color: "var(--text-muted)"}}>{t("achievementDescription__" + a.achievement)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const placementRankClass =
    placement === 1 ? "awards-placement-gold"
    : placement === 2 ? "awards-placement-silver"
    : placement === 3 ? "awards-placement-bronze"
    : "awards-placement-default";

  const pageRankClass =
    placement === 1 ? "awards-rank-gold"
    : placement === 2 ? "awards-rank-silver"
    : placement === 3 ? "awards-rank-bronze"
    : "";

  const playerHeader = currentPlayer ? (
    <div className="awards-header">
      <div className={"awards-placement " + placementRankClass}>
        <span className="awards-placement-number">{placement}.</span>
      </div>
      <div className="awards-player">
        <ReactCountryFlag
          countryCode={currentPlayer.nationality ?? "UN"}
          svg
          title={currentPlayer.nationality ?? "UN"}
          className="awards-flag"
        />
        <span>{playerToString(currentPlayer)}</span>
      </div>
    </div>
  ) : null;

  const playerBody = isMobile ? (
    <Tabs defaultActiveKey="awards-matches" id="awards-ceremony-mobile-tabs" variant="tabs" fill className="mt-3">
      <Tab eventKey="awards-matches" title={t("matches")}>{matchesPanel}</Tab>
      <Tab eventKey="awards-stats" title={t("statistics")}>{statsPanel}</Tab>
    </Tabs>
  ) : (
    <div className="awards-two-col mt-4">
      <div className="awards-col-left">{matchesPanel}</div>
      <div className="awards-col-right">{statsPanel}</div>
    </div>
  );

  const playerPage = currentPlayer ? (
    <>
      {playerHeader}
      {funFact && (
        <div className="awards-fun-fact">{t(funFact.key, funFact.params)}</div>
      )}
      {playerBody}
    </>
  ) : null;

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
  };

  return (
    <div className={isMobile ? "awards-ceremony awards-ceremony-mobile" : "awards-ceremony"}>
      <div className={"awards-stage " + pageRankClass}>
        <div className="awards-glow" aria-hidden="true" />
        <button
          type="button"
          className="awards-nav-arrow awards-nav-left"
          onClick={goPrev}
          disabled={currentIndex === 0}
          aria-label={t("previousPlayer")}
        >
          ‹
        </button>
        <button
          type="button"
          className="awards-nav-arrow awards-nav-right"
          onClick={goNext}
          disabled={currentIndex === totalPages - 1}
          aria-label={t("nextPlayer")}
        >
          ›
        </button>
        <AnimatePresence initial={false} custom={directionRef.current} mode="popLayout">
          <motion.div
            key={currentIndex}
            custom={directionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "tween", duration: 0.35, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.2 },
            }}
            className="awards-page"
          >
            {isIntro ? introPage : playerPage}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AwardsCeremonyTab;
